/**
 * ScopePropertyRow - the inner of one Notion-style property row in the deal Scope doc's
 * property-panel groups (Company Overview / Primary Contact): a label cell + a value cell. The
 * packages/ui `propertyRow` node view supplies the 2-column grid wrapper + the collapsed styling.
 *
 * The controls are hand-built to be BORDERLESS and to look identical to the read-only view value, so
 * editing feels seamless (nothing "turns into a form field"):
 *  - text / number / autocomplete -> a transparent inline text input that reads like plain text.
 *  - date / currency -> the formatted value as plain text; click to edit in place.
 *  - single-select (select / pill_radio) -> the value as a pill; click the pill to pick another.
 *  - multi-select (multi_select / pill_select / …) -> the values as pills each with an inline [x],
 *    plus a "Choose" button that opens a checkable menu to add more.
 *
 * The pills match the view pill styling (.rte-scope-chip); the menus reuse the Radix Popover +
 * shared `dropdown-content` chrome. Values are written through the same `onChange(fieldKey, value)`
 * answer path as the Questionnaire tab (Zod-backed field model), so the two tabs stay in sync. When
 * `onChange` is absent (viewer) every control renders as static view text/pills.
 */

import * as React from 'react';
import { parseCurrencyValue } from '@protoss/shared';
import {
  AutocompleteField,
  CheckIcon,
  ChevronDownIcon,
  Cross1Icon,
  DynamicSearchField,
  MagnifyingGlassIcon,
  PlusIcon,
  Popover,
} from '@protoss/ui';

import type { SalesDealTemplateFieldSummary } from '../services/salesApi';
import { getAnswerKey, resolveFieldDisplayValue } from '../utils/field-display-value';
import {
  normalizeMultiSelectValue,
  normalizeTextValue,
} from '../utils/questionnaire-field-visibility';
import type { QuestionnaireAnswerMap } from '../utils/questionnaire-field-visibility';

/**
 * The autocomplete machinery (from useQuestionnaireAutocomplete) threaded into the panel so the
 * street-address (Google Places) and entity (company:* / customer:email) autocompletes behave
 * exactly like the Questionnaire tab.
 */
export interface ScopePropertyAutocomplete {
  /** source -> suggestion labels (place results are updated live as the user types). */
  values: Record<string, string[]>;
  /** Places selection -> decompose + populate sibling address fields. */
  onPlaceSelect: (fieldKey: string, label: string) => void;
  /** customer:email selection -> populate sibling contact fields. */
  onCustomerSelect: (fieldKey: string, email: string) => void;
  /** True while a Google Places lookup is in-flight. */
  placesLoading: boolean;
}

export interface ScopePropertyRowProps {
  field: SalesDealTemplateFieldSummary;
  answers: QuestionnaireAnswerMap;
  /**
   * Live answer write - same signature/path as the Questionnaire tab's onAnswerChange. Omitted for a
   * read-only viewer, in which case the value renders as static text/pills instead of a control.
   */
  onChange?: (fieldKey: string, value: unknown) => void;
  /** Present only when editable; drives the place/entity autocomplete controls. */
  autocomplete?: ScopePropertyAutocomplete;
}

/** Currency codes offered by the picker (mirrors @protoss/ui CURRENCY_OPTIONS). */
const CURRENCY_CHOICES: Array<{ code: string; symbol: string }> = [
  { code: 'USD', symbol: '$' },
  { code: 'EUR', symbol: '€' },
  { code: 'GBP', symbol: '£' },
  { code: 'JPY', symbol: '¥' },
  { code: 'CAD', symbol: 'CA$' },
];

/** Prepend https:// when a URL field value has no scheme (mirrors the questionnaire's normalizer). */
function normalizeUrl(v: string): string {
  const trimmed = v.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  return `https://${trimmed}`;
}

/** Controls that resolve to a single string choice ("choose one" - rendered as a clickable pill). */
const SINGLE_CHOICE_CONTROLS = new Set(['select', 'pill_radio', 'stack_option']);
/** Controls that resolve to a list of string choices ("choose one or more" - rendered as pills). */
const MULTI_CHOICE_CONTROLS = new Set([
  'multi_select',
  'pill_select',
  'pill_dropdown',
  'tag_input',
]);

/** Current single value regardless of scalar/array storage. */
function singleValueOf(raw: unknown): string {
  if (Array.isArray(raw)) return typeof raw[0] === 'string' ? raw[0] : '';
  return typeof raw === 'string' ? raw : '';
}

function optionLabel(field: SalesDealTemplateFieldSummary, value: string): string {
  return field.options.find((o) => o.value === value)?.label ?? value;
}

const EmptyValue = () => <span className="scope-prop-value__empty">Empty</span>;

// ── Single select: a pill you click to change ────────────────────────────────

function SingleSelectControl({
  field,
  raw,
  onCommit,
}: {
  field: SalesDealTemplateFieldSummary;
  raw: unknown;
  onCommit: (value: unknown) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const current = singleValueOf(raw);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className="scope-prop-trigger">
          {current ? (
            <span className="scope-prop-pill">{optionLabel(field, current)}</span>
          ) : (
            <EmptyValue />
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="dropdown-content scope-prop-menu"
          align="start"
          sideOffset={4}
          collisionPadding={8}
        >
          {field.options.map((option) => (
            <button
              key={option.value}
              type="button"
              className="scope-prop-menu__item"
              onClick={() => {
                onCommit(option.value);
                setOpen(false);
              }}
            >
              <span className="scope-prop-pill">{option.label}</span>
              {option.value === current ? (
                <CheckIcon className="scope-prop-menu__check" />
              ) : null}
            </button>
          ))}
          {current ? (
            <button
              type="button"
              className="scope-prop-menu__clear"
              onClick={() => {
                onCommit(null);
                setOpen(false);
              }}
            >
              Clear
            </button>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// ── Multi select: pills with inline [x] + a Choose menu ──────────────────────

/** Free-text add row shown in the Choose menu for multi fields with no fixed option list. */
function FreeTextAddRow({ onAdd }: { onAdd: (value: string) => void }) {
  const [text, setText] = React.useState('');
  const submit = () => {
    const v = text.trim();
    if (v) onAdd(v);
    setText('');
  };
  return (
    <div className="scope-prop-menu__add">
      <input
        className="scope-prop-menu__add-input"
        autoFocus
        value={text}
        placeholder="Type and press Enter"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
      />
      <button type="button" className="scope-prop-menu__add-btn" onClick={submit} disabled={!text.trim()}>
        Add
      </button>
    </div>
  );
}

function MultiSelectControl({
  field,
  raw,
  onCommit,
  autocomplete,
}: {
  field: SalesDealTemplateFieldSummary;
  raw: unknown;
  onCommit: (value: unknown) => void;
  autocomplete?: ScopePropertyAutocomplete;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const values = normalizeMultiSelectValue(raw);
  const setValues = (next: string[]) => onCommit(next.length > 0 ? next : null);
  const toggle = (value: string) =>
    setValues(values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);

  // Options: static field.options, else the pre-fetched autocomplete list for the field's source
  // (e.g. geographies -> geocoding countries, mirroring the Questionnaire tab's picklist). A field
  // with neither (a source that yields no list) falls back to free-text add.
  const options: Array<{ value: string; label: string }> =
    field.options.length > 0
      ? field.options.map((o) => ({ value: o.value, label: o.label }))
      : field.autocompleteSource
        ? (autocomplete?.values[field.autocompleteSource] ?? []).map((s) => ({ value: s, label: s }))
        : [];
  // Creatable field ("+ Add option"): the search box doubles as the create input, and a typed value
  // with no match offers a Create row. New values persist to the field's options on save (server).
  const creatable = !!field.displayHints?.allowCustomOptions;
  const freeAdd = options.length === 0 && !creatable;
  const showSearch = creatable || options.length > 8;
  const trimmedQuery = query.trim();
  const filtered = trimmedQuery
    ? options.filter((o) => o.label.toLowerCase().includes(trimmedQuery.toLowerCase()))
    : options;
  const canCreate =
    creatable &&
    !!trimmedQuery &&
    !options.some(
      (o) => o.label.toLowerCase() === trimmedQuery.toLowerCase() || o.value === trimmedQuery
    );
  const createOption = () => {
    if (!trimmedQuery) return;
    if (!values.includes(trimmedQuery)) setValues([...values, trimmedQuery]);
    setQuery('');
  };

  const labelFor = (value: string) =>
    options.find((o) => o.value === value)?.label ?? optionLabel(field, value);

  return (
    <div className="scope-prop-pills">
      {values.map((value) => (
        <span key={value} className="scope-prop-pill scope-prop-pill--removable">
          {labelFor(value)}
          <button
            type="button"
            className="scope-prop-pill__remove"
            aria-label={`Remove ${labelFor(value)}`}
            onClick={() => setValues(values.filter((v) => v !== value))}
          >
            <Cross1Icon />
          </button>
        </span>
      ))}
      <Popover.Root
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery('');
        }}
      >
        <Popover.Trigger asChild>
          <button type="button" className="scope-prop-choose">
            <PlusIcon className="scope-prop-choose__icon" />
            {values.length === 0 ? 'Choose' : null}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="dropdown-content scope-prop-menu"
            align="start"
            sideOffset={4}
            collisionPadding={8}
          >
            {freeAdd ? (
              <FreeTextAddRow
                onAdd={(v) => {
                  if (!values.includes(v)) setValues([...values, v]);
                }}
              />
            ) : (
              <>
                {showSearch ? (
                  <input
                    className="scope-prop-menu__search"
                    autoFocus
                    value={query}
                    placeholder={creatable ? 'Search or add…' : 'Search…'}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && canCreate) {
                        e.preventDefault();
                        createOption();
                      }
                    }}
                  />
                ) : null}
                <div className="scope-prop-menu__list">
                  {filtered.map((option) => {
                    const checked = values.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className="scope-prop-menu__item"
                        onClick={() => toggle(option.value)}
                      >
                        <span className="scope-prop-pill">{option.label}</span>
                        {checked ? <CheckIcon className="scope-prop-menu__check" /> : null}
                      </button>
                    );
                  })}
                  {canCreate ? (
                    <button
                      type="button"
                      className="scope-prop-menu__create"
                      onClick={createOption}
                    >
                      <PlusIcon className="scope-prop-menu__create-icon" />
                      Create &ldquo;{trimmedQuery}&rdquo;
                    </button>
                  ) : null}
                  {filtered.length === 0 && !canCreate ? (
                    <span className="scope-prop-menu__empty">No matches</span>
                  ) : null}
                </div>
              </>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}

// ── Inline text (borderless, reads like the value) ───────────────────────────

function InlineTextControl({
  value,
  placeholder,
  multiline,
  onCommit,
}: {
  value: string;
  placeholder: string;
  multiline?: boolean;
  onCommit: (value: string) => void;
}) {
  if (multiline) {
    return (
      <textarea
        className="scope-prop-input scope-prop-input--area"
        rows={1}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onCommit(e.target.value)}
      />
    );
  }
  return (
    <input
      className="scope-prop-input"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onCommit(e.target.value)}
    />
  );
}

/** Formatted value shown as text; click to reveal a real input in place (date / currency). */
function InlineEditableText({
  display,
  children,
}: {
  display: string;
  /** Render the editing input given a request to stop editing. */
  children: (stopEditing: () => void) => React.ReactNode;
}) {
  const [editing, setEditing] = React.useState(false);
  if (editing) return <>{children(() => setEditing(false))}</>;
  return (
    <button type="button" className="scope-prop-textbtn" onClick={() => setEditing(true)}>
      {display ? display : <EmptyValue />}
    </button>
  );
}

function sanitizeAmount(input: string): string {
  const cleaned = input.replace(/[^0-9.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  // Keep only the first decimal point.
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
}

// ── Currency: a currency-code picker in front + a borderless amount input ─────

function CurrencyControl({
  raw,
  defaultCurrency,
  onCommit,
}: {
  raw: unknown;
  defaultCurrency: string;
  onCommit: (value: unknown) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const cv = parseCurrencyValue(raw, defaultCurrency);
  const currentSymbol = CURRENCY_CHOICES.find((c) => c.code === cv.currency)?.symbol ?? '';
  return (
    <div className="scope-prop-currency">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button type="button" className="scope-prop-currency__code">
            <span className="scope-prop-currency__code-symbol">{currentSymbol}</span>
            {cv.currency}
            <ChevronDownIcon className="scope-prop-currency__chevron" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="dropdown-content scope-prop-menu"
            align="start"
            sideOffset={4}
            collisionPadding={8}
          >
            {CURRENCY_CHOICES.map((choice) => (
              <button
                key={choice.code}
                type="button"
                className="scope-prop-menu__item scope-prop-currency__option"
                onClick={() => {
                  onCommit({ amount: cv.amount, currency: choice.code });
                  setOpen(false);
                }}
              >
                <span className="scope-prop-currency__option-label">
                  <span className="scope-prop-currency__option-symbol">{choice.symbol}</span>
                  {choice.code}
                </span>
                {choice.code === cv.currency ? (
                  <CheckIcon className="scope-prop-menu__check" />
                ) : null}
              </button>
            ))}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <input
        className="scope-prop-input scope-prop-currency__amount"
        inputMode="decimal"
        value={cv.amount}
        placeholder="0"
        onChange={(e) => onCommit({ amount: sanitizeAmount(e.target.value), currency: cv.currency })}
      />
    </div>
  );
}

// ── Number: integer-only inline input (no stepper, no wrapping error row) ─────

function NumberControl({
  value,
  placeholder,
  onCommit,
}: {
  value: string;
  placeholder: string;
  onCommit: (value: string) => void;
}) {
  return (
    <input
      className="scope-prop-input"
      inputMode="numeric"
      value={value}
      placeholder={placeholder}
      // Validation is enforced at the input: only digits are accepted, so the value can never be
      // non-numeric and no separate error row is needed.
      onChange={(e) => onCommit(e.target.value.replace(/[^0-9]/g, ''))}
    />
  );
}

// ── Autocomplete: Google Places (street) + entity/url (industry, website, …) ──

function AutocompleteControl({
  field,
  answerKey,
  raw,
  autocomplete,
  onChange,
}: {
  field: SalesDealTemplateFieldSummary;
  answerKey: string;
  raw: unknown;
  autocomplete: ScopePropertyAutocomplete;
  onChange: (fieldKey: string, value: unknown) => void;
}) {
  const source = field.autocompleteSource;
  const rawValue = normalizeTextValue(raw);
  const placeholder = field.placeholder ?? 'Empty';
  const suggestions =
    source === 'field:options'
      ? field.options.map((o) => o.label)
      : source
        ? (autocomplete.values[source] ?? [])
        : [];

  // Google Places street search: type -> live suggestions; select -> decompose + fill siblings.
  if (source === 'geocoding:placeAutocomplete') {
    return (
      <div className="scope-prop-autocomplete">
        <DynamicSearchField
          value={rawValue}
          suggestions={suggestions}
          placeholder={placeholder}
          size="compact"
          loading={autocomplete.placesLoading}
          onChange={(v) => onChange(answerKey, v)}
          onSelect={(selected) => autocomplete.onPlaceSelect(answerKey, selected)}
        />
      </div>
    );
  }

  // `validator` is typed as 'email' in the summary but the template also carries 'url' (website);
  // the questionnaire reads it via a cast for the same reason.
  const isUrl = (field.displayHints as Record<string, unknown> | null)?.validator === 'url';
  const isCustomerEmail = source === 'customer:email';
  const display = isUrl ? rawValue.replace(/^https?:\/\//i, '') : rawValue;
  return (
    <div className="scope-prop-autocomplete">
      <AutocompleteField
        value={display}
        options={isUrl ? suggestions.map((s) => s.replace(/^https?:\/\//i, '')) : suggestions}
        ariaLabel={field.label}
        // URL fields render an https:// prefix; a placeholder would sit under it, so leave it blank
        // (the prefix is the empty-state affordance).
        placeholder={isUrl ? '' : placeholder}
        size="compact"
        prefix={isUrl ? 'https://' : undefined}
        normalize={isUrl ? normalizeUrl : undefined}
        onChange={(v) => onChange(answerKey, isUrl ? (v ? normalizeUrl(v) : v) : v)}
        onSelect={(selected) => {
          if (isCustomerEmail) autocomplete.onCustomerSelect(answerKey, selected);
        }}
      />
    </div>
  );
}

function ScopePropertyValueControl({
  field,
  answers,
  onChange,
  autocomplete,
}: ScopePropertyRowProps) {
  const answerKey = getAnswerKey(field);
  const raw = answers[answerKey] ?? null;

  // Read-only (viewer): render the view projection - pills for choices, text otherwise.
  if (!onChange) {
    const dv = resolveFieldDisplayValue(field, answers);
    if (dv.kind === 'pills') {
      return (
        <div className="scope-prop-pills">
          {dv.pills.map((pill, i) => (
            <span key={`${pill}-${i}`} className="scope-prop-pill">
              {pill}
            </span>
          ))}
        </div>
      );
    }
    const text = dv.kind === 'text' ? dv.text : '';
    return text ? <span className="scope-prop-readonly">{text}</span> : <EmptyValue />;
  }

  const commit = (value: unknown) => onChange(answerKey, value);
  const placeholder = field.placeholder ?? 'Empty';

  if (SINGLE_CHOICE_CONTROLS.has(field.uiControl)) {
    return <SingleSelectControl field={field} raw={raw} onCommit={commit} />;
  }

  if (MULTI_CHOICE_CONTROLS.has(field.uiControl)) {
    return (
      <MultiSelectControl field={field} raw={raw} onCommit={commit} autocomplete={autocomplete} />
    );
  }

  if (field.uiControl === 'autocomplete' && autocomplete) {
    return (
      <AutocompleteControl
        field={field}
        answerKey={answerKey}
        raw={raw}
        autocomplete={autocomplete}
        onChange={onChange}
      />
    );
  }

  if (field.uiControl === 'currency') {
    return (
      <CurrencyControl
        raw={raw}
        defaultCurrency={field.displayHints?.defaultCurrency ?? 'USD'}
        onCommit={commit}
      />
    );
  }

  if (field.uiControl === 'number') {
    return (
      <NumberControl value={normalizeTextValue(raw)} placeholder={placeholder} onCommit={commit} />
    );
  }

  if (field.uiControl === 'date') {
    const dv = resolveFieldDisplayValue(field, answers);
    return (
      <InlineEditableText display={dv.kind === 'text' ? dv.text : ''}>
        {(stop) => (
          <input
            type="date"
            autoFocus
            className="scope-prop-input"
            value={normalizeTextValue(raw)}
            onChange={(e) => commit(e.target.value)}
            onBlur={stop}
          />
        )}
      </InlineEditableText>
    );
  }

  // text, textarea, autocomplete (viewer / no context), and any other text-shaped control.
  return (
    <InlineTextControl
      value={normalizeTextValue(raw)}
      placeholder={placeholder}
      multiline={field.uiControl === 'textarea'}
      onCommit={commit}
    />
  );
}

export function ScopePropertyRow({
  field,
  answers,
  onChange,
  autocomplete,
}: ScopePropertyRowProps) {
  // The autocomplete affordance lives in the KEY column (right-aligned), not baked into the control:
  // a magnifier for the address place-search, an "auto" tag for the entity autocompletes. Editable
  // rows only (a read-only viewer has no autocomplete).
  const indicator: 'search' | 'auto' | null =
    onChange && field.uiControl === 'autocomplete'
      ? field.autocompleteSource === 'geocoding:placeAutocomplete'
        ? 'search'
        : 'auto'
      : null;
  return (
    <>
      <div className="scope-prop-row__label">
        <span className="scope-prop-row__label-text">{field.label}</span>
        {indicator === 'auto' ? (
          <span className="scope-prop-row__label-badge">auto</span>
        ) : indicator === 'search' ? (
          <MagnifyingGlassIcon className="scope-prop-row__label-icon" />
        ) : null}
      </div>
      <div className="scope-prop-row__value">
        <ScopePropertyValueControl
          field={field}
          answers={answers}
          onChange={onChange}
          autocomplete={autocomplete}
        />
      </div>
    </>
  );
}
